import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logoFull from '../assets/logo.png';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { loginOtp, verifyOtp } = useAuth();

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset state each time modal opens + lock body scroll
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setOtpCode('');
      setOtpSent(false);
      setLoading(false);
      setError('');
      setSuccess('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Esc key closes
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const msg = await loginOtp(email);
      setOtpSent(true);
      setSuccess(msg);
    } catch (err: any) {
      setError(err.response?.data?.error || "Erreur lors de l'envoi du code OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await verifyOtp(email, otpCode);
      onClose();
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code OTP invalide');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="login-modal-backdrop"
      // Backdrop click ne ferme PAS le popup (demande du client)
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(13, 34, 55, 0.55)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, animation: 'loginFade 0.25s ease-out'
      }}
    >
      <div
        className="login-card"
        style={{ position: 'relative', maxWidth: 430, width: '100%', margin: 0, animation: 'loginPop 0.28s cubic-bezier(0.25,1.15,0.42,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Bouton de fermeture */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          style={{
            position: 'absolute', top: 12, right: 12, width: 34, height: 34,
            border: 'none', borderRadius: '50%', background: 'rgba(0,0,0,0.06)',
            color: '#555', fontSize: 20, cursor: 'pointer', lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s'
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.12)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.06)')}
        >
          &times;
        </button>

        <img src={logoFull} alt="FORGEST" style={{ display: 'block', margin: '0 auto 5px', width: 200 }} />
        <p className="login-subtitle">Plateforme Globale de Gestion</p>

        {error && <div className="login-error">{error}</div>}
        {success && <div className="login-success">{success}</div>}

        {!otpSent ? (
          <form onSubmit={handleRequestOtp}>
            <div className="login-field">
              <label>Adresse email</label>
              <input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Envoi en cours...' : 'Recevoir le code OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <div className="login-field">
              <label>Adresse email</label>
              <input type="email" value={email} disabled />
            </div>
            <div className="login-field">
              <label>Code OTP (4 chiffres)</label>
              <input
                type="text"
                placeholder="0000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                maxLength={4}
                required
                className="login-otp-input"
                autoFocus
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Verification...' : 'Se connecter'}
            </button>
            <button
              type="button"
              className="login-resend"
              onClick={() => handleRequestOtp()}
              disabled={loading}
            >
              Renvoyer le code
            </button>
            <button
              type="button"
              className="login-resend"
              onClick={() => { setOtpSent(false); setOtpCode(''); setError(''); setSuccess(''); }}
              style={{ marginTop: 4 }}
            >
              Changer d'adresse email
            </button>
          </form>
        )}

        <div className="login-footer">
          FORGEST &copy; {new Date().getFullYear()} - Gestion des forages
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
